import React, { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { useSeo } from "@/lib/useSeo";
import { Hero, ProcessSteps, TrustSection, FinalCTA, FamilyTestimonials } from "@/components/public/sections";
import { CoverageChecker } from "@/components/public/CoverageChecker";
import { NearbyTerritories } from "@/components/public/NearbyTerritories";
import { CalloutBanner, LocalIntro, LocalTeam, LocalPartners, LocalResources, LocalFaqs } from "@/components/public/LocalContentBlocks";
import { ServiceAreaMap } from "@/components/public/ServiceAreaMap";
import { IntakeForm } from "@/components/public/IntakeForm";
import { Phone, Clock, MapPin, ArrowRight, Loader2, Compass } from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (p) => `tel:${(p || "").replace(/[^0-9+]/g, "")}`;

export default function LocalPage() {
  const { country, state, city } = useParams();
  const { openChat } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      const { data } = await publicApi.get(`/public/local/${country}/${state}/${city}`);
      setData(data);
      window.scrollTo({ top: 0 });
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally { setLoading(false); }
  }, [country, state, city]);

  useEffect(() => { load(); }, [load]);

  useSeo({
    title: data?.seo?.title, description: data?.seo?.description,
    canonical: data?.canonical, noindex: notFound || data?.noindex, jsonLd: data?.json_ld,
  });

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (notFound || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="local-not-found">
        <Compass size={40} className="text-[#9BB9CE]" />
        <h1 className="mt-4 text-2xl font-display font-bold text-[#1C4358]">We're not in this area yet</h1>
        <p className="mt-2 text-[#4B5563] max-w-md">We couldn't find a local MovingMyMama page for that location. Explore our services or book a free consultation.</p>
        <Link to="/book" className="mt-6 inline-flex items-center gap-2 px-7 h-12 rounded-full text-white bg-[#9C1E22] hover:bg-[#861A1D] font-semibold">Book Free Consultation</Link>
      </div>
    );
  }

  const { content, local, lanes, territory } = data;
  const label = territory.label;

  return (
    <div data-testid="local-page">
      {/* Local context bar */}
      <div className="bg-[#1C4358] text-white" data-testid="local-context-bar">
        <div className={`${CONTAINER} py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-[13.5px]`}>
          <span className="inline-flex items-center gap-1.5 font-semibold" data-testid="local-label"><MapPin size={14} /> Serving {label}</span>
          {local.phone && <a href={telHref(local.phone)} data-testid="local-phone-link" className="inline-flex items-center gap-1.5 hover:text-[#E0A526]"><Phone size={14} /> {local.phone}</a>}
          {local.service_availability && <span className="inline-flex items-center gap-1.5 text-white/85"><Clock size={14} /> {local.service_availability}</span>}
        </div>
      </div>

      <CalloutBanner text={local.callout_banner} />

      <div className="relative">
        <Hero content={content} onChat={openChat} franchise={data.franchise} />
      </div>

      <LocalIntro text={local.intro_copy} city={territory.city} />

      {/* Local service lanes */}
      <section className="py-14 sm:py-20" data-testid="local-lanes">
        <div className={CONTAINER}>
          <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">How we help in {territory.city}</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">Local support for your family's next move</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {lanes.map((l) => (
              <Link key={l.local_slug} to={l.href} data-testid={`local-lane-${l.local_slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-[#1C4358]/10 shadow-sm hover:shadow-md hover:border-[#1C4358]/25 transition-all">
                {l.hero_image_url && (
                  <div className="h-40 overflow-hidden">
                    <img src={l.hero_image_url} alt={l.name} loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-[#1C4358] text-[18px]">{l.name}</h3>
                  {l.hero_subheadline && <p className="mt-2 text-[15px] text-[#4B5563] leading-relaxed line-clamp-3 flex-1">{l.hero_subheadline}</p>}
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#9C1E22]">Learn more <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ServiceAreaMap
        coverage={data.coverage}
        label={label}
        onCta={() => document.getElementById("coverage")?.scrollIntoView({ behavior: "smooth", block: "start" })}
      />

      <div id="coverage" className="scroll-mt-24">
        <CoverageChecker franchise={data.franchise} cityId={local.city_id} label={label} />
      </div>

      <ProcessSteps content={content} />
      <TrustSection content={content} />
      <LocalTeam team={local.team_display} />
      <LocalPartners partners={local.partner_display} />
      <LocalResources links={local.resource_links} />
      <LocalFaqs faqs={local.faq_additions} />
      <FamilyTestimonials content={content} onChat={openChat} franchise={data.franchise} />
      <NearbyTerritories nearby={data.nearby} heading="Nearby Areas We Serve" />

      {/* Local booking */}
      <section id="book" className="py-16 sm:py-20 bg-white" data-testid="local-book">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">Book a free consultation in {territory.city}</h2>
            <p className="mt-2 text-[16px] text-[#4B5563]">Tell us what's happening and your local guide will reach out — no cost, no pressure.</p>
          </div>
          <div className="mt-8">
            <IntakeForm
              cta="Local Landing — Book" source="local_landing_page" franchise={data.franchise}
              extra={{
                country_id: local.country_id, state_province_id: local.state_province_id,
                metro_region_id: local.metro_region_id, city_id: local.city_id,
                service_area_id: local.service_area_id, local_path: territory.base_path,
              }}
            />
          </div>
        </div>
      </section>

      <FinalCTA content={content} />
    </div>
  );
}
